import {isNotNullOrUndefined, isTruthy} from '@wandb/weave/common/util/types';

import {toRunsDataQueryConfig} from '../../containers/RunsDataLoader';
import {And, getUniqueKeysUsedInFilter} from '../../util/filters';
import {type Filter, type ValueOp} from '../../util/filterTypes';
import {createPreloadableLazyComponent} from '../../util/lazyImport';
import {useSampleAndQueryToTable} from '../../util/panelHelpers';
import type {PanelSpec} from '../../util/panelTypes';
import {type Query} from '../../util/queryTypes';
import {keyFromString} from '../../util/runs';
import {type Key, type Value} from '../../util/runTypes';
import {getTitleFromConfig, type ScatterPlotConfig} from './common';
import {getScatterPanelColorField} from './settings/scatterColorDisplay';
export type {ScatterPlotConfig};

const PANEL_TYPE = 'Scatter Plot';

function scatterPlotTransformQuery(query: Query, config: ScatterPlotConfig) {
  const result = toRunsDataQueryConfig(query, undefined, {
    page: {size: 500},
    panelType: PANEL_TYPE,
  });

  const {axisKeys, legendKeys, groupingKeys, selectionKeys, colorKeys} =
    getKeysForQuery(query, config);

  const displayKeys = [
    ...axisKeys,
    ...legendKeys,
    ...groupingKeys,
    ...selectionKeys,
    ...colorKeys,
  ];
  result.configKeys = [
    ...new Set(
      displayKeys
        .filter(key => key.section === 'config')
        .map(key => key.name.split('[')?.[0])
    ),
  ];
  result.summaryKeys = [
    ...new Set(
      displayKeys.filter(key => key.section === 'summary').map(key => key.name)
    ),
  ];

  const firstQuery = result.runsetQueries[0];
  // Color keys are fetched for typing / category inference but are not
  // required for a point to appear (missing color → excluded bucket).
  const filterKeys = [...axisKeys, ...legendKeys, ...selectionKeys];
  if (firstQuery != null && filterKeys.length > 0) {
    firstQuery.filters = addRequiredKeysToFilter(
      firstQuery.filters,
      filterKeys
    );
  }

  return result;
}

function getKeysForQuery(
  query: Query,
  config: ScatterPlotConfig
): {
  axisKeys: Key[];
  legendKeys: Key[];
  groupingKeys: Key[];
  selectionKeys: Key[];
  colorKeys: Key[];
} {
  const axisKeys = [config.xAxis, config.yAxis, config.zAxis]
    .filter(isTruthy)
    .map(keyFromString)
    .filter(isNotNullOrUndefined)
    .filter(key => ['config', 'summary'].includes(key.section));

  const legendKeys =
    config.legendFields?.map(keyFromString).filter(isNotNullOrUndefined) ?? [];

  // We need the grouping values to color properly
  const groupingKeys =
    query.runSets?.flatMap?.(rs => rs.grouping).filter(isNotNullOrUndefined) ??
    [];

  // we need the keys from selections to color the visible runs differently
  // We look for the selections in the first runset which is probably
  // something we should change.  query.selections appears to be undefined.
  const firstRunsetSelections = query.runSets?.[0]?.selections;
  const selectionKeys =
    firstRunsetSelections != null
      ? getUniqueKeysUsedInFilter(firstRunsetSelections)
      : [];

  // Discrete / Continuous Color live on semanticLegendSettings, not zAxis.
  // Fetch them so filtered runs carry values for type inference and categories.
  const colorField = getScatterPanelColorField(config);
  const colorKeys =
    colorField != null
      ? [keyFromString(colorField)]
          .filter(isNotNullOrUndefined)
          .filter(key => ['config', 'summary'].includes(key.section))
      : [];

  return {
    axisKeys,
    legendKeys,
    groupingKeys,
    selectionKeys,
    colorKeys,
  };
}

function addRequiredKeysToFilter(f: Filter<Key>, keys: Key[]): Filter<Key> {
  return And([
    f,
    ...keys.map(key => ({
      key,
      op: '!=' as ValueOp,
      value: null as Value,
    })),
  ]);
}

function useTableData(pageQuery: Query, config: ScatterPlotConfig) {
  const query = scatterPlotTransformQuery(pageQuery, config);

  return useSampleAndQueryToTable(query, pageQuery, config);
}

const configSpec = {
  chartTitle: {
    editor: 'string' as const,
    displayName: 'Chart title',
  },
  xAxisLabel: {editor: 'string' as const, displayName: 'X axis label'},
  yAxisLabel: {editor: 'string' as const, displayName: 'Y axis label'},
  zAxisLabel: {editor: 'string' as const, displayName: 'Z axis label'},
  xAxisMin: {editor: 'number' as const, displayName: 'X min'},
  xAxisMax: {editor: 'number' as const, displayName: 'X max'},
  xAxisLogScale: {
    editor: 'checkbox' as const,
    displayName: 'X log scale',
    default: false,
  },
  yAxisMin: {editor: 'number' as const, displayName: 'Y min'},
  yAxisMax: {editor: 'number' as const, displayName: 'Y max'},
  yAxisLogScale: {
    editor: 'checkbox' as const,
    displayName: 'Y log scale',
    default: false,
  },
  zAxisMin: {editor: 'number' as const, displayName: 'Z min'},
  zAxisMax: {editor: 'number' as const, displayName: 'Z max'},
  zAxisLogScale: {
    editor: 'checkbox' as const,
    displayName: 'Z log scale',
    default: false,
  },
  customGradient: {
    editor: 'gradient' as const,
    displayName: 'Gradient',
    default: [
      {offset: 0, color: '#900000'},
      {offset: 50, color: '#D64F04'},
      {offset: 100, color: '#FFE600'},
    ],
  },
  showMaxYAxisLine: {
    editor: 'checkbox' as const,
    displayName: 'Plot max y',
    default: false,
  },
  showMinYAxisLine: {
    editor: 'checkbox' as const,
    displayName: 'Plot min y',
    default: false,
  },
  showAvgYAxisLine: {
    editor: 'checkbox' as const,
    displayName: 'Plot avg y',
    default: false,
  },
};

export const Spec: PanelSpec<typeof PANEL_TYPE, ScatterPlotConfig> = {
  type: PANEL_TYPE,
  Component: createPreloadableLazyComponent(() => import('./Component'))
    .Component,
  getTitleFromConfig,

  exportable: {
    image: true,
    csv: true,
    api: true,
  },

  transformQuery: scatterPlotTransformQuery,
  configSpec,
  useTableData,
  icon: 'panel-scatter-plot',
};
